import { useEffect, useState, useCallback } from 'react';

// Fenêtre de validité d'une notif cliquée en démarrage à froid. 10 s ne suffisait PAS :
// ouvrir la PWA depuis l'écran verrouillé (chargement + auth + réseau) dépasse souvent 10 s,
// et la popup ne s'affichait alors jamais. 2 min = large, et on purge après lecture.
const COLD_START_WINDOW_MS = 120000;

export interface NotificationPayload {
  type: string;
  title: string;
  body: string;
  ts?: string;
  url?: string;
  [key: string]: any;
}

/**
 * Hook pour gérer l'affichage d'une popup de notification.
 * Écoute les messages du service worker ET IndexedDB pour le démarrage à froid.
 *
 * Deux mécanismes :
 * 1. App déjà ouverte : SW postMessage NOTIFICATION_CLICK au client
 * 2. Démarrage à froid : SW stocke le payload en IndexedDB, le hook le récupère
 *
 * Retourne: { notification, isOpen, close }
 */
export function useNotificationDetail() {
  const [notification, setNotification] = useState<NotificationPayload | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const closeNotification = useCallback(() => {
    setNotification(null);
    setIsOpen(false);
    // Nettoyer le stockage temporaire
    sessionStorage.removeItem('nudge-pending-notification');
  }, []);

  useEffect(() => {
    // 1. Écouter les messages du service worker
    const handleMessage = (event: MessageEvent) => {
      // App déjà ouverte : notif cliquée
      if (event.data?.type === 'NOTIFICATION_CLICK' && event.data?.notification) {
        setNotification(event.data.notification);
        setIsOpen(true);
      }

      // Démarrage à froid : SW envoie le payload directement si la fenetre s'ouvre assez vite
      if (event.data?.type === 'NOTIFICATION_PENDING' && event.data?.payload) {
        setNotification(event.data.payload);
        setIsOpen(true);
        // Aussi stocker en sessionStorage au cas où
        sessionStorage.setItem('nudge-pending-notification', JSON.stringify({
          payload: event.data.payload,
          ts: Date.now(),
        }));
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleMessage);

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    // 2. Vérifier IndexedDB pour les notifs en attente (démarrage à froid)
    // Utilise un fallback sessionStorage au cas où IndexedDB ne soit pas disponible
    const checkPendingNotification = () => {
      try {
        // D'abord essayer sessionStorage (fallback rapide)
        const stored = sessionStorage.getItem('nudge-pending-notification');
        if (stored) {
          try {
            const data = JSON.parse(stored);
            if (data?.payload && data.ts && Date.now() - data.ts < COLD_START_WINDOW_MS) {
              setNotification(data.payload);
              setIsOpen(true);
              sessionStorage.removeItem('nudge-pending-notification');
            }
            return;
          } catch (e) {
            sessionStorage.removeItem('nudge-pending-notification');
          }
        }

        // Ensuite IndexedDB
        const request = indexedDB.open('nudge-notifications', 1);

        // La base est normalement créée par le service worker (store « pending »). Mais si le SW
        // n'est pas encore passé, cet open la crée VIDE : le store n'existe pas, et le
        // `transaction('pending')` ci-dessous levait un NotFoundError DANS un callback async,
        // donc hors du try/catch → erreur non rattrapée qui cassait le rendu et vidait l'écran
        // (incident mobile « plus aucun post nulle part », 25/08/2026). On crée le store nous-mêmes
        // et on vérifie qu'il est là avant de lire.
        request.onupgradeneeded = (e) => {
          const db = (e.target as IDBOpenDBRequest).result;
          if (!db.objectStoreNames.contains('pending')) db.createObjectStore('pending', { keyPath: 'id' });
        };

        request.onsuccess = (e) => {
          const db = (e.target as IDBOpenDBRequest).result;
          if (!db.objectStoreNames.contains('pending')) { db.close(); return; }
          let getReq: IDBRequest;
          try {
            const tx = db.transaction('pending', 'readonly');
            getReq = tx.objectStore('pending').get('latest');
          } catch (err) {
            console.debug('[useNotificationDetail] IndexedDB indisponible:', err);
            db.close();
            return;
          }

          getReq.onsuccess = () => {
            const data = (getReq as any).result;
            if (data?.payload && data.ts && Date.now() - data.ts < COLD_START_WINDOW_MS) {
              // Notif reçue dans les 10 dernières secondes → l'afficher
              setNotification(data.payload);
              setIsOpen(true);

              // Nettoyer IndexedDB après lecture
              try {
                const delTx = db.transaction('pending', 'readwrite');
                delTx.objectStore('pending').delete('latest');
              } catch (e) {
                console.debug('[useNotificationDetail] Cleanup failed:', e);
              }
            }
          };

          getReq.onerror = () => {
            console.debug('[useNotificationDetail] IndexedDB read failed');
          };
        };

        request.onerror = () => {
          console.debug('[useNotificationDetail] IndexedDB open failed');
        };
      } catch (e) {
        console.debug('[useNotificationDetail] Pending notification check failed:', e);
      }
    };

    // Petit délai initial pour laisser le temps au SW d'initialiser IndexedDB
    const initialTimer = setTimeout(checkPendingNotification, 150);

    // Retry après 1s au cas où IndexedDB ne serait pas prêt tout de suite
    const retryTimer = setTimeout(checkPendingNotification, 1000);

    return () => {
      clearTimeout(initialTimer);
      clearTimeout(retryTimer);
    };
  }, []);

  return { notification, isOpen, close: closeNotification };
}
